import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Col, Container, media, Row, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {InlineTextLink} from 'UI/components/action/InlineTextLink';
import {Background} from 'UI/components/layout/Background';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadVideo} from 'UI/components/layout/PayloadVideo';
import {StorePartner} from 'UI/components/layout/StorePartner';
import {H2, P} from 'UI/components/layout/Text';
import {TimeBanner} from 'UI/components/layout/TimeBanner';

import {useHero} from './useHero';
import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';
import {StoreFinder} from 'Images/icons';

import type {HeroBlock} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected by the `Hero` component.
 * It outlines all required and optional properties needed to render the hero section with background, content, and partner logos.
 * This interface leverages the `HeroBlock` type from the Payload CMS schema to ensure type consistency between the CMS data and the component.
 */
interface ComponentProps {
    /**
     * The optional background color value applied when the background type is set to 'Color'.
     */
    backgroundColor?: HeroBlock['backgroundColor'];
    /**
     * The optional background image media object used when the background type is set to 'Image'.
     */
    backgroundImage?: HeroBlock['backgroundImage'];
    /**
     * The background type determining whether the hero displays a video, image, or solid color background.
     */
    backgroundType: HeroBlock['backgroundType'];
    /**
     * The optional background video object containing desktop and mobile video sources for video backgrounds.
     */
    backgroundVideo?: HeroBlock['backgroundVideo'];
    /**
     * The headline text displayed as the main title of the hero section.
     */
    headline: HeroBlock['headline'];
    /**
     * The introductory text displayed below the headline providing additional context or description.
     */
    intro: HeroBlock['intro'];
    /**
     * An array of partner objects, each containing an ID, logo media, and website URL for store links.
     */
    partners: HeroBlock['partners'];
    /**
     * The product image object containing the image media data to be displayed prominently in the hero section.
     */
    productImage: HeroBlock['productImage'];
    /**
     * A boolean flag indicating whether to show a link to the store finder section, allowing users to locate physical retail locations.
     */
    showStoreFinderLink: HeroBlock['showStoreFinderLink'];
    /**
     * A unique identifier assigned to the component for testing purposes, enabling reliable element targeting in automated tests.
     *
     * @default 'Hero'
     */
    testId?: string;
    /**
     * The optional theme data attribute applied to the component for switching between light and dark color schemes.
     */
    theme?: HeroBlock['theme'];
    /**
     * The optional text content to be displayed in the time-limited promotional banner above the headline.
     */
    timebanner?: HeroBlock['timebanner'];
}

/**
 * The `Hero` component renders the main hero section of the promotional landing page, featuring a configurable background, product imagery, headline content, and partner store links.
 * It supports three background modes (video, image, and solid color) and automatically plays background videos when the video type is selected.
 * The component also displays an optional time-limited promotional banner and provides navigation to a store finder section for locating physical retail locations.
 *
 * @param props                 The component props.
 * @param props.backgroundColor The optional background color value used when `backgroundType` is 'Color'.
 * @param props.backgroundImage The optional background image media object used when `backgroundType` is 'Image'.
 * @param props.backgroundType  The background rendering mode determining whether a video, image, or color is displayed.
 * @param props.backgroundVideo The optional video object containing desktop and mobile video sources for video backgrounds.
 * @param props.headline        The main headline text displayed prominently in the hero section.
 * @param props.intro           The introductory description text rendered below the headline.
 * @param props.partners        An array of partner store objects containing logos and website URLs for direct shop links.
 * @param props.productImage    The product image data object displayed as the primary visual in the hero section.
 * @param props.testId          An optional unique identifier for testing purposes, defaulting to `'Hero'`.
 * @param props.theme           The optional theme attribute for switching between light and dark color schemes.
 * @param props.timebanner      The optional promotional banner text displayed above the headline.
 * @returns A JSX element representing the hero section with background, product image, content, and partner links.
 *
 * @example
 * ```tsx
 * <Hero
 *   backgroundColor="#000000"
 *   backgroundType="Video"
 *   backgroundVideo={{
 *     desktop: {url: '/video/desktop.webm'},
 *     desktopFallback: {url: '/video/desktop.mp4'},
 *     mobile: {url: '/video/mobile.webm'},
 *     mobileFallback: {url: '/video/mobile.mp4'}
 *   }}
 *   headline="Sonos Era 300"
 *   intro="Experience spatial audio like never before."
 *   partners={[
 *     {id: '1', logo: mediaObject, website: 'https://store.example.com'}
 *   ]}
 *   productImage={productImageObject}
 *   theme="dark"
 *   timebanner="Limited Time Offer"
 * />
 * ```
 */
const Hero = observer(({
    backgroundColor,
    backgroundImage,
    backgroundType,
    backgroundVideo,
    headline,
    intro,
    partners,
    productImage,
    showStoreFinderLink,
    testId = 'Hero',
    theme,
    timebanner
}: ComponentProps) => {
    const {aspectRatio, colors, videoRef} = useHero(productImage);

    return (
        <Wrapper
            data-cy={testId}
            data-nfq-theme={theme}
            id="shops"
        >
            {backgroundType === 'Video' && backgroundVideo && (
                <StyledVideo ref={videoRef} controls={false} video={backgroundVideo} loop muted playsInline />
            )}
            {(backgroundType === 'Image' || backgroundType === 'Color') && (
                <Background
                    backgroundColor={backgroundColor}
                    backgroundImage={backgroundImage}
                    backgroundType={backgroundType}
                />
            )}
            <Container>
                <Spacer y={10} />
                <Row>
                    <Col justify="center" lg={7} xs={12}>
                        <ImageWrapper>
                            <HeroImageWrapper $aspectRatio={aspectRatio}>
                                <Image image={productImage} />
                            </HeroImageWrapper>
                        </ImageWrapper>
                    </Col>
                    <Col lg={5} xs={12} xxl={4}>
                        <Spacer
                            y={{
                                xs: 10, lg: 2
                            }}
                            isNotStretching
                        />
                        {timebanner && (
                            <>
                                <TimeBanner>{timebanner}</TimeBanner>
                                <Spacer y={6} isNotStretching />
                            </>
                        )}
                        <H2 as="h1"><Color $color={colors.primary}>{headline}</Color></H2>
                        {intro && (
                            <>
                                <Spacer y={6} isNotStretching />
                                <Description $size="medium">
                                    <Color $color={colors.primary}>{intro}</Color>
                                </Description>
                            </>
                        )}
                        <Spacer y={10} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.primary}>
                                Hier geht es direkt zu den Online Shops:
                            </Color>
                        </P>
                        <Spacer y={4} isNotStretching />
                        <StoreCardWrapper>
                            {partners?.map(partner => {
                                if (!isDefinedAndPopulated(partner)) return null;

                                return (
                                    <StorePartner
                                        key={partner.id}
                                        logo={partner.logo}
                                        url={partner.website}
                                    />
                                );
                            })}
                        </StoreCardWrapper>
                        {showStoreFinderLink && (
                            <>
                                <Spacer y={12} isNotStretching />
                                <StoreFinderWrapper>
                                    <Color $color={colors.primary}>
                                        <StoreFinder color1="currentColor" />
                                    </Color>
                                    <Spacer x={6} isNotStretching />
                                    <P $size="medium">
                                        <Color $color={colors.primary}>
                                            Jetzt zum
                                            <InlineTextLink href="#store-finder">Store Finder</InlineTextLink>{' '}
                                            und einen Markt in deiner Nähe finden
                                        </Color>
                                    </P>
                                </StoreFinderWrapper>
                            </>
                        )}
                    </Col>
                </Row>
            </Container>
        </Wrapper>
    );
});

Hero.displayName = 'Hero';

export {Hero};

const Wrapper = styled.section`
    isolation: isolate;
    padding-bottom: ${spacing(14)};
    padding-top: ${spacing(14)};
    position: relative;

    ${media('md')} {
        padding-bottom: ${spacing(20)};
    }
`;

const StyledVideo = styled(PayloadVideo)`
    height: 100%;
    left: 0;
    object-fit: cover;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
`;

const ImageWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
`;

interface HeroImageWrapperProps {
    $aspectRatio: string;
}

const HeroImageWrapper = styled.div<HeroImageWrapperProps>`
    aspect-ratio: ${({$aspectRatio}) => $aspectRatio};
    margin-top: -2%;
    max-width: 100%;
    width: 100%;
`;

const Image = styled(PayloadImage)`
    height: 100%;
    object-fit: cover;
    width: 100%;
`;

const Description = styled(P)`
    ${media('lg')} {
        max-width: 42rem;
    }
`;

const StoreCardWrapper = styled.div`
    display: grid;
    gap: ${spacing(4)};
    grid-template-columns: repeat(1, 1fr);

    ${media('sm')} {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const StoreFinderWrapper = styled.div`
    align-items: center;
    display: flex;
    justify-content: flex-start;
`;